/**
 * ExportPanel - Step 5: Export to PDF
 */

import React from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  PictureAsPdf as PdfIcon,
  Timer as TimerIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { ProjectData } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  projectId: string;
  project: ProjectData;
}

const ExportPanel: React.FC<Props> = ({ projectId, project }) => {
  const { token, refreshUserInfo } = useAuth();
  const [isExporting, setIsExporting] = React.useState(false);
  const [exportStatus, setExportStatus] = React.useState<string>('');
  const [error, setError] = React.useState<string | null>(null);
  const [userLimits, setUserLimits] = React.useState<{
    status: string;
    projects_sent: number;
    max_projects: number;
    remaining_projects: number;
  } | null>(null);

  // Fetch user limits on mount
  React.useEffect(() => {
    const fetchUserInfo = async () => {
      if (!token) return;
      
      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (response.ok) {
          const userInfo = await response.json();
          setUserLimits({
            status: userInfo.status || 'free',
            projects_sent: userInfo.projects_sent || 0,
            max_projects: userInfo.max_projects || 1,
            remaining_projects: userInfo.remaining_projects || 1,
          });
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    
    fetchUserInfo();
  }, [token]);

  const exportToPDF = async () => {
    if (!projectId || projectId === '') {
      setError('Please generate the project first (go to Step 4: Preview & Generate)');
      return;
    }

    setIsExporting(true);
    setError(null);
    setExportStatus('Generating DOCX document...');

    try {
      // Export and download directly
      const exportResponse = await fetch(
        `${API_URL}/api/project/${projectId}/export/pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!exportResponse.ok) {
        const errorData = await exportResponse.text();
        let errorMessage = 'Export failed';
        try {
          const parsed = JSON.parse(errorData);
          errorMessage = parsed.detail || parsed.error || errorMessage;
        } catch {
          errorMessage = errorData || errorMessage;
        }
        throw new Error(errorMessage);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = exportResponse.headers.get('Content-Disposition');
      let filename = `Erasmus_Application_${projectId.substring(0, 8)}.docx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Download the file
      const blob = await exportResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setExportStatus('Document downloaded successfully!');
      
      // Update user limits after successful export
      await refreshUserInfo();
      const userInfoResponse = await fetch(`${API_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (userInfoResponse.ok) {
        const userInfo = await userInfoResponse.json();
        setUserLimits({
          status: userInfo.status || 'free',
          projects_sent: userInfo.projects_sent || 0,
          max_projects: userInfo.max_projects || 1,
          remaining_projects: userInfo.remaining_projects || 1,
        });
      }

    } catch (err: any) {
      setError(err.message || 'Export failed');
      setExportStatus('');
    } finally {
      setIsExporting(false);
    }
  };

  const calculateEstimatedPages = () => {
    if (!project.sections) return 0;
    const totalWords = Object.values(project.sections).reduce(
      (sum, section) => sum + section.split(' ').length,
      0
    );
    return Math.ceil(totalWords / 350); // ~350 words per page
  };

  const estimatedPages = calculateEstimatedPages();
  const totalWords = project.sections 
    ? Object.values(project.sections).reduce((sum, s) => sum + s.split(' ').length, 0)
    : 0;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" gutterBottom sx={{ color: 'white', fontWeight: 600 }}>
        Export to Word
      </Typography>
      <Typography variant="body2" sx={{ mb: 3, color: 'white !important' }}>
        Generate and download your professional Erasmus+ application document in Word format
      </Typography>

      {/* User Status and Limits */}
      {userLimits && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: userLimits.status === 'payed' ? 'success.light' : 'info.light' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Account Status: <span style={{ textTransform: 'capitalize' }}>{userLimits.status}</span>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Projects sent: {userLimits.projects_sent} / {userLimits.max_projects}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" color={userLimits.remaining_projects > 0 ? 'success.main' : 'error.main'}>
                {userLimits.remaining_projects} remaining
              </Typography>
              {userLimits.remaining_projects === 0 && userLimits.status === 'free' && (
                <Typography variant="caption" color="text.secondary">
                  Upgrade to send more
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Document Statistics */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          📊 Document Statistics
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <PdfIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary="Estimated Pages"
              secondary={`~${estimatedPages} pages (target: 25-30)`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Total Words"
              secondary={`${totalWords.toLocaleString()} words`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText
              primary="Sections Completed"
              secondary={`${project.sections ? Object.keys(project.sections).length : 0} / 10 sections`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <TimerIcon color="action" />
            </ListItemIcon>
            <ListItemText
              primary="Delivery Method"
              secondary="Direct download (Word DOCX format)"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Export Options */}
      <Paper sx={{ p: 3, mb: 3, textAlign: 'center', bgcolor: 'success.light' }}>
        <Typography variant="h6" gutterBottom>
          🎉 Ready to Export!
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Your professional Erasmus+ application will be generated and downloaded:
        </Typography>
        <Box sx={{ textAlign: 'left', maxWidth: 500, mx: 'auto', mb: 3 }}>
          <Typography variant="body2">✅ EU/Erasmus+ professional formatting</Typography>
          <Typography variant="body2">✅ All mandatory sections included</Typography>
          <Typography variant="body2">✅ Page numbers and headers</Typography>
          <Typography variant="body2">✅ Professional cover page</Typography>
          <Typography variant="body2">✅ Tables and structured content</Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 600, color: 'primary.main' }}>
            💾 Document will be saved to your downloads folder
          </Typography>
        </Box>

        <Button
            variant="contained"
            size="large"
            color="success"
            startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={exportToPDF}
            disabled={
              isExporting || 
              project.status !== 'ready' || 
              (userLimits ? userLimits.remaining_projects === 0 : false)
            }
          >
            {isExporting 
              ? exportStatus 
              : userLimits && userLimits.remaining_projects === 0
              ? 'Limit Reached'
              : 'Generate & Download DOCX'}
          </Button>

        {exportStatus && exportStatus.includes('downloaded') && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ {exportStatus}
          </Alert>
        )}
      </Paper>

      {/* Quality Checklist */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          ✅ Pre-Submission Checklist
        </Typography>
        <Divider sx={{ my: 2 }} />
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color="success" />
            </ListItemIcon>
            <ListItemText primary="All mandatory sections completed" />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color={estimatedPages >= 25 && estimatedPages <= 30 ? 'success' : 'warning'} />
            </ListItemIcon>
            <ListItemText 
              primary="Page count within recommended range"
              secondary={estimatedPages < 25 ? "Document might be too short" : estimatedPages > 30 ? "Document might be too long" : "Perfect length"}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color={project.work_packages && project.work_packages.length >= 6 ? 'success' : 'warning'} />
            </ListItemIcon>
            <ListItemText 
              primary="Work Packages defined"
              secondary={`${project.work_packages?.length || 0} WPs (recommended: 6-8)`}
            />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckIcon color={project.basic_info?.partners.length && project.basic_info.partners.length >= 2 ? 'success' : 'warning'} />
            </ListItemIcon>
            <ListItemText 
              primary="Partner consortium complete"
              secondary={`${(project.basic_info?.partners.length || 0) + 1} organisations`}
            />
          </ListItem>
        </List>
      </Paper>

      {estimatedPages < 25 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          ⚠️ Your document is shorter than recommended (25 pages minimum). Consider adding more detail to key sections.
        </Alert>
      )}

      {estimatedPages > 30 && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          ⚠️ Your document is longer than recommended (30 pages maximum). Consider condensing some sections.
        </Alert>
      )}

      <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
        <Typography variant="body2">
          💡 <strong>After Export:</strong> Open the DOCX in Word/LibreOffice, add your logo, make final adjustments, and convert to PDF before submission to the National Agency.
        </Typography>
      </Box>
    </Box>
  );
};

export default ExportPanel;

